import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Globe,
  TrendingUp,
  Flame,
  DollarSign,
  BarChart3,
  Activity,
  ExternalLink,
  LogIn,
  KeyRound,
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import TradingViewChart from "@/components/TradingViewChart";

const CriticalMacroIndicators = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold tracking-tight">Critical Macro Indicators</h1>
            <p className="text-xs text-muted-foreground">
              Real-time geopolitical, inflation, energy, equity & bond dashboards
            </p>
          </div>
          <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-500/30 bg-emerald-500/10">
            <Activity className="h-3 w-3" /> Live
          </Badge>
        </div>
      </div>

      <div className="max-w-[1800px] mx-auto px-4 py-6 space-y-8">

        {/* ── External Services — Login / Sign Up ── */}
        <Card className="border-dashed border-primary/30 bg-primary/5">
          <Collapsible defaultOpen>
            <CollapsibleTrigger asChild>
              <CardHeader className="pb-3 cursor-pointer hover:bg-primary/5 rounded-t-lg transition-colors">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">External Services — Login Required</CardTitle>
                  <Badge variant="outline" className="text-primary border-primary/30 bg-primary/10 text-[10px]">
                    <LogIn className="h-3 w-3 mr-1" /> Sign in first
                  </Badge>
                </div>
                <CardDescription>
                  Some data sources below require a free or paid account. Log in before viewing charts &amp; data.
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    {
                      name: "Polymarket",
                      desc: "Free account to view & trade prediction markets",
                      loginUrl: "https://polymarket.com/sign-in",
                      signupUrl: "https://polymarket.com/sign-up",
                      color: "blue",
                    },
                    {
                      name: "Truflation",
                      desc: "Free account for the inflation dashboard",
                      loginUrl: "https://app.truflation.com/login",
                      signupUrl: "https://app.truflation.com/register",
                      color: "amber",
                    },
                    {
                      name: "MacroMicro",
                      desc: "Free tier for basic charts (premium for full access)",
                      loginUrl: "https://en.macromicro.me/login",
                      signupUrl: "https://en.macromicro.me/register",
                      color: "emerald",
                    },
                    {
                      name: "TradingView",
                      desc: "Free account for interactive charts & watchlists",
                      loginUrl: "https://www.tradingview.com/accounts/signin/",
                      signupUrl: "https://www.tradingview.com/accounts/signup/",
                      color: "violet",
                    },
                    {
                      name: "Yardeni Research",
                      desc: "Free & premium access to market strategy reports and PE data",
                      loginUrl: "https://www.yardeni.com/login/",
                      signupUrl: "https://www.yardeni.com/register/",
                      color: "rose",
                    },
                    {
                      name: "Multpl.com",
                      desc: "Free access — no account needed, but bookmark for quick reference",
                      loginUrl: "https://www.multpl.com/",
                      signupUrl: "https://www.multpl.com/",
                      color: "indigo",
                    },
                  ].map((svc) => (
                    <div
                      key={svc.name}
                      className="rounded-lg border bg-card p-4 space-y-3"
                    >
                      <div className="flex items-center gap-2">
                        <LogIn className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold text-sm text-foreground">{svc.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{svc.desc}</p>
                      <div className="flex gap-2">
                        <a href={svc.loginUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                          <Button variant="outline" size="sm" className="w-full gap-1 text-xs">
                            <LogIn className="h-3 w-3" /> Log In
                          </Button>
                        </a>
                        <a href={svc.signupUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                          <Button variant="default" size="sm" className="w-full gap-1 text-xs">
                            <ExternalLink className="h-3 w-3" /> Sign Up
                          </Button>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* ── 1. Polymarket — US & Iran Ceasefire ── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-500" />
              <CardTitle className="text-lg">Geopolitical Risk — US × Iran Ceasefire</CardTitle>
              <Badge variant="secondary" className="text-[10px]">Polymarket</Badge>
            </div>
            <CardDescription>
              Prediction-market odds on a US–Iran ceasefire. Click below to view the live market on Polymarket.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a
                href="https://polymarket.com/event/us-x-iran-ceasefire-by"
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-lg border bg-muted/30 p-6 hover:border-blue-500/40 hover:bg-blue-500/5 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="text-blue-600 border-blue-500/30 bg-blue-500/10">Live Market</Badge>
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-blue-500 transition-colors" />
                </div>
                <h4 className="font-semibold text-foreground mb-1">US × Iran Ceasefire</h4>
                <p className="text-xs text-muted-foreground">View real-time prediction odds and trade on Polymarket</p>
              </a>
              <a
                href="https://polymarket.com/predictions/diplomacy-ceasefire"
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-lg border bg-muted/30 p-6 hover:border-blue-500/40 hover:bg-blue-500/5 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="text-blue-600 border-blue-500/30 bg-blue-500/10">All Markets</Badge>
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-blue-500 transition-colors" />
                </div>
                <h4 className="font-semibold text-foreground mb-1">Iran Ceasefire Predictions</h4>
                <p className="text-xs text-muted-foreground">Browse all Iran-related ceasefire prediction markets</p>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* ── 2. Truflation TruCPI-US ── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-amber-500" />
              <CardTitle className="text-lg">Truflation US CPI Inflation Index (TruCPI-US)</CardTitle>
              <Badge variant="secondary" className="text-[10px]">Truflation</Badge>
            </div>
            <CardDescription>
              Real-time, on-chain inflation tracking — an alternative measure to official CPI data.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="https://truflation.com/marketplace/us-inflation-rate"
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-lg border bg-muted/30 p-6 hover:border-amber-500/40 hover:bg-amber-500/5 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="text-amber-600 border-amber-500/30 bg-amber-500/10">Index</Badge>
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-amber-500 transition-colors" />
                </div>
                <h4 className="font-semibold text-foreground mb-1">TruCPI-US Index</h4>
                <p className="text-xs text-muted-foreground">Live US CPI inflation rate with category breakdown</p>
              </a>
              <a
                href="https://en.macromicro.me/charts/116496/us-truflation-index-yoy"
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-lg border bg-muted/30 p-6 hover:border-amber-500/40 hover:bg-amber-500/5 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="text-amber-600 border-amber-500/30 bg-amber-500/10">YoY Chart</Badge>
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-amber-500 transition-colors" />
                </div>
                <h4 className="font-semibold text-foreground mb-1">Truflation YoY on MacroMicro</h4>
                <p className="text-xs text-muted-foreground">Year-over-year chart with interactive tools</p>
              </a>
              <a
                href="https://app.truflation.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-lg border bg-muted/30 p-6 hover:border-amber-500/40 hover:bg-amber-500/5 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="text-amber-600 border-amber-500/30 bg-amber-500/10">Dashboard</Badge>
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-amber-500 transition-colors" />
                </div>
                <h4 className="font-semibold text-foreground mb-1">Truflation Dashboard</h4>
                <p className="text-xs text-muted-foreground">Full inflation analytics dashboard</p>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* ── 3. BofA Global Fund Manager Survey — Cash Holdings ── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-rose-500" />
              <CardTitle className="text-lg">BofA Global Fund Manager Survey — Average Cash Holdings</CardTitle>
              <Badge variant="secondary" className="text-[10px]">BofA / Proprietary</Badge>
            </div>
            <CardDescription>
              10-year trend of average cash allocation from BofA's monthly Global Fund Manager Survey.
              This is proprietary data — view the latest via the links below.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg border bg-muted/30 p-6 text-center space-y-3">
              <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                The BofA FMS Cash Holdings chart is proprietary. Access via the following sources:
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <a
                  href="https://www.google.com/search?q=BofA+Global+Fund+Manager+Survey+Average+Cash+Holdings+chart"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm" className="gap-1">
                    <ExternalLink className="h-3 w-3" /> Search Latest Chart
                  </Button>
                </a>
                <a
                  href="https://x.com/search?q=BofA%20Fund%20Manager%20Survey%20cash&f=image"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm" className="gap-1">
                    <ExternalLink className="h-3 w-3" /> X / Twitter Images
                  </Button>
                </a>
                <a
                  href="https://www.zerohedge.com/s/BofA+fund+manager+survey+cash"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm" className="gap-1">
                    <ExternalLink className="h-3 w-3" /> ZeroHedge
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── 4. TTF1! Dutch TTF Natural Gas — Weekly + Ichimoku & ES1! ── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-500" />
              <CardTitle className="text-lg">Energy & Futures — TTF Natural Gas & E-mini S&P 500</CardTitle>
              <Badge variant="secondary" className="text-[10px]">TradingView</Badge>
            </div>
            <CardDescription>
              Dutch TTF Natural Gas Calendar Month Futures (Weekly, Ichimoku) and ES E-mini S&P 500 Futures.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <TradingViewChart
              symbol="NYMEX:TTF1!"
              interval="W"
              title="TTF1! — Weekly (Ichimoku + RSI)"
              studies={["IchimokuCloud@tv-basicstudies"]}
              height={520}
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TradingViewChart
                symbol="CME_MINI:ES1!"
                interval="W"
                title="ES1! — Weekly (Ichimoku + RSI)"
                studies={["IchimokuCloud@tv-basicstudies"]}
                height={450}
              />
              <TradingViewChart
                symbol="CME_MINI:ES1!"
                interval="D"
                title="ES1! — Daily (RSI)"
                height={450}
              />
            </div>
          </CardContent>
        </Card>

        {/* ── 5. S5INFT — S&P 500 Info Tech ── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-violet-500" />
              <CardTitle className="text-lg">S&P 500 Information Technology — S5INFT</CardTitle>
              <Badge variant="secondary" className="text-[10px]">TradingView</Badge>
            </div>
            <CardDescription>Weekly and Daily views of the S&P 500 IT sector index with RSI.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TradingViewChart
                symbol="SP:S5INFT"
                interval="W"
                title="S5INFT — Weekly"
                height={450}
              />
              <TradingViewChart
                symbol="SP:S5INFT"
                interval="D"
                title="S5INFT — Daily"
                height={450}
              />
            </div>
          </CardContent>
        </Card>

        {/* ── 6. Qatar Stock Exchange ── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-teal-500" />
              <CardTitle className="text-lg">Qatar Stock Exchange Index (QSI)</CardTitle>
              <Badge variant="secondary" className="text-[10px]">TradingView</Badge>
            </div>
            <CardDescription>Daily, Weekly & Monthly views of the Qatar Stock Exchange index with RSI.</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="daily" className="w-full">
              <TabsList className="mb-3">
                <TabsTrigger value="daily">Daily</TabsTrigger>
                <TabsTrigger value="weekly">Weekly</TabsTrigger>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
              </TabsList>
              <TabsContent value="daily">
                <TradingViewChart symbol="QSE:GNRI" interval="D" title="Qatar Exchange — Daily" height={500} />
              </TabsContent>
              <TabsContent value="weekly">
                <TradingViewChart symbol="QSE:GNRI" interval="W" title="Qatar Exchange — Weekly" height={500} />
              </TabsContent>
              <TabsContent value="monthly">
                <TradingViewChart symbol="QSE:GNRI" interval="M" title="Qatar Exchange — Monthly" height={500} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* ── 7 (was 6). US02Y — US 2-Year Treasury ── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-sky-500" />
              <CardTitle className="text-lg">US 2-Year Treasury Yield — US02Y</CardTitle>
              <Badge variant="secondary" className="text-[10px]">TradingView</Badge>
            </div>
            <CardDescription>Daily and Weekly US 2-Year Treasury yield with RSI.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TradingViewChart
                symbol="TVC:US02Y"
                interval="D"
                title="US02Y — Daily"
                height={450}
              />
              <TradingViewChart
                symbol="TVC:US02Y"
                interval="W"
                title="US02Y — Weekly"
                height={450}
              />
            </div>
          </CardContent>
        </Card>

        {/* ── 8 (was 7). S&P 500 Forward P/E, Shiller PE ── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-indigo-500" />
              <CardTitle className="text-lg">S&P 500 Valuation — Forward P/E & Shiller CAPE</CardTitle>
              <Badge variant="secondary" className="text-[10px]">Multiple Sources</Badge>
            </div>
            <CardDescription>
              Forward 12-month P/E ratio (10-year trend), PE Forward, and Shiller CAPE ratio.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a
                href="https://www.multpl.com/s-p-500-pe-ratio"
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-lg border bg-muted/30 p-6 hover:border-indigo-500/40 hover:bg-indigo-500/5 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="text-indigo-600 border-indigo-500/30 bg-indigo-500/10">PE Ratio</Badge>
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-indigo-500 transition-colors" />
                </div>
                <h4 className="font-semibold text-foreground mb-1">S&P 500 PE Ratio — 10 Year</h4>
                <p className="text-xs text-muted-foreground">Historical trailing P/E ratio with interactive chart on Multpl.com</p>
              </a>
              <a
                href="https://www.multpl.com/shiller-pe"
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-lg border bg-muted/30 p-6 hover:border-indigo-500/40 hover:bg-indigo-500/5 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="text-indigo-600 border-indigo-500/30 bg-indigo-500/10">Shiller CAPE</Badge>
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-indigo-500 transition-colors" />
                </div>
                <h4 className="font-semibold text-foreground mb-1">Shiller CAPE Ratio</h4>
                <p className="text-xs text-muted-foreground">Cyclically adjusted P/E ratio — long-term valuation measure</p>
              </a>
            </div>

            {/* Forward PE via Yardeni-style external link */}
            <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                S&P 500 Forward 12-Month P/E Ratio
              </h3>
              <p className="text-sm text-muted-foreground">
                The Forward P/E data is sourced from FactSet / Yardeni Research. Access via:
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://www.yardeni.com/pub/stockmktperatio.pdf" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="gap-1">
                    <ExternalLink className="h-3 w-3" /> Yardeni Research PDF
                  </Button>
                </a>
                <a href="https://www.currentmarketvaluation.com/models/price-earnings.php" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="gap-1">
                    <ExternalLink className="h-3 w-3" /> Current Market Valuation
                  </Button>
                </a>
                <a href="https://www.multpl.com/s-p-500-pe-ratio" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="gap-1">
                    <ExternalLink className="h-3 w-3" /> Multpl.com
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default CriticalMacroIndicators;
